/**
 * Content Primitives
 *
 * Building blocks for displaying content:
 * - Text: Basic text with optional styling
 * - Heading: Section headings (h1-h4)
 * - Divider: Horizontal separator
 * - Badge: Small label/tag
 * - Alert: Highlighted message
 * - Code: Code/monospace text
 */

import React from 'react';

export function Text({
  size = 'sm', // xs, sm, base, lg
  weight, // normal, medium, semibold, bold
  color, // text, muted, primary, success, warning, error
  align,
  truncate,
  children,
  style = {},
}) {
  const colorMap = {
    text: 'var(--color-text)',
    muted: 'var(--color-text-muted)',
    primary: 'var(--color-primary)',
    success: 'var(--color-success)',
    warning: 'var(--color-warning)',
    error: 'var(--color-error)',
  };

  return (
    <span
      style={{
        fontSize: `var(--text-${size})`,
        fontWeight: weight ? `var(--font-${weight})` : undefined,
        color: color ? colorMap[color] : undefined,
        textAlign: align,
        ...(truncate && {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
        }),
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export function Heading({
  level = 2, // 1, 2, 3, 4
  icon,
  children,
  style = {},
}) {
  const sizes = {
    1: 'var(--text-xl)',
    2: 'var(--text-lg)',
    3: 'var(--text-base)',
    4: 'var(--text-sm)',
  };

  const Tag = `h${level}`;

  return (
    <Tag
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        fontSize: sizes[level],
        fontWeight: 'var(--font-semibold)',
        color: 'var(--color-text)',
        margin: 0,
        marginBottom: 'var(--space-2)',
        ...style,
      }}
    >
      {icon && <span>{icon}</span>}
      {children}
    </Tag>
  );
}

export function Divider({ margin = 3, style = {} }) {
  return (
    <hr
      style={{
        border: 'none',
        borderTop: '1px solid var(--color-border)',
        margin: `var(--space-${margin}) 0`,
        ...style,
      }}
    />
  );
}

export function Badge({
  text,
  color = 'default', // default, primary, success, warning, error
  size = 'sm', // xs, sm
  icon,
  children,
  style = {},
}) {
  const colors = {
    default: {
      bg: 'var(--color-surface-hover)',
      text: 'var(--color-text-muted)',
    },
    primary: {
      bg: 'rgba(var(--color-primary-rgb), 0.1)',
      text: 'var(--color-primary)',
    },
    success: {
      bg: 'rgba(var(--color-success-rgb), 0.1)',
      text: 'var(--color-success)',
    },
    warning: {
      bg: 'rgba(var(--color-warning-rgb), 0.1)',
      text: 'var(--color-warning)',
    },
    error: {
      bg: 'rgba(var(--color-error-rgb), 0.1)',
      text: 'var(--color-error)',
    },
  };

  const c = colors[color] || colors.default;
  const padding = size === 'xs' ? '1px 4px' : '2px 8px';
  const fontSize = size === 'xs' ? '10px' : 'var(--text-xs)';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding,
        fontSize,
        fontWeight: 'var(--font-medium)',
        borderRadius: 'var(--radius-full)',
        background: c.bg,
        color: c.text,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon && <span>{icon}</span>}
      {text || children}
    </span>
  );
}

export function Alert({
  type = 'info', // info, success, warning, error
  title,
  icon,
  children,
  style = {},
}) {
  const types = {
    info: {
      bg: 'rgba(var(--color-primary-rgb), 0.1)',
      border: 'rgba(var(--color-primary-rgb), 0.2)',
      text: 'var(--color-primary)',
      defaultIcon: 'ℹ️',
    },
    success: {
      bg: 'rgba(var(--color-success-rgb), 0.1)',
      border: 'rgba(var(--color-success-rgb), 0.2)',
      text: 'var(--color-success)',
      defaultIcon: '✓',
    },
    warning: {
      bg: 'rgba(var(--color-warning-rgb), 0.1)',
      border: 'rgba(var(--color-warning-rgb), 0.2)',
      text: 'var(--color-warning)',
      defaultIcon: '⚠️',
    },
    error: {
      bg: 'rgba(var(--color-error-rgb), 0.1)',
      border: 'rgba(var(--color-error-rgb), 0.2)',
      text: 'var(--color-error)',
      defaultIcon: '✕',
    },
  };

  const t = types[type] || types.info;

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: t.bg,
        border: `1px solid ${t.border}`,
        ...style,
      }}
    >
      <span style={{ fontSize: '16px' }}>{icon || t.defaultIcon}</span>
      <div style={{ flex: 1 }}>
        {title && (
          <div
            style={{
              fontWeight: 'var(--font-semibold)',
              fontSize: 'var(--text-sm)',
              color: t.text,
              marginBottom: children ? '4px' : 0,
            }}
          >
            {title}
          </div>
        )}
        {children && (
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

export function Code({ language, children, style = {} }) {
  return (
    <pre
      style={{
        fontFamily: 'monospace',
        fontSize: 'var(--text-xs)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        overflow: 'auto',
        margin: 0,
        ...style,
      }}
    >
      <code>{children}</code>
    </pre>
  );
}
